import { ConflictResponse } from './types';

/**
 * Base error for all conflict-related errors.
 * Thrown when conflict resolution fails or is not possible.
 */
export class ConflictError extends Error {
    constructor(
        public readonly conflictResponse: ConflictResponse,
        public readonly attemptNumber: number,
        message?: string
    ) {
        super(
            message ||
            `Conflict on ${conflictResponse.entityType}#${conflictResponse.entityId} after ${attemptNumber} attempts`
        );
        this.name = 'ConflictError';
        
        // Maintain proper stack trace for where error was thrown (only available on V8)
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, ConflictError);
        }
    }

    /**
     * Get a user-friendly error message.
     */
    getUserMessage(): string {
        const { entityType, conflictType } = this.conflictResponse;
        
        if (conflictType === 'ENTITY_DELETED') {
            return `This ${entityType.toLowerCase()} has been deleted and can no longer be updated.`;
        }
        
        return `This ${entityType.toLowerCase()} was modified by another user. Please refresh and try again.`;
    }
}

/**
 * Error thrown when no conflict handler is registered for an entity type.
 */
export class NoConflictHandlerError extends ConflictError {
    constructor(conflictResponse: ConflictResponse) {
        super(
            conflictResponse,
            0,
            `No conflict handler registered for entity type: ${conflictResponse.entityType}`
        );
        this.name = 'NoConflictHandlerError';
    }
}

/**
 * Error thrown when maximum retry attempts are exceeded.
 */
export class MaxRetriesExceededError extends ConflictError {
    constructor(conflictResponse: ConflictResponse, maxRetries: number) {
        super(
            conflictResponse,
            maxRetries,
            `Conflict resolution failed after ${maxRetries} attempts for ${conflictResponse.entityType}#${conflictResponse.entityId}`
        );
        this.name = 'MaxRetriesExceededError';
    }
}
